import { ChevronIcon } from "vega-icons";

import { useBoolean } from "@vega-core/hooks/useBoolean";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

import { filledTypeConfig, NoticeType } from "./noticeConfig";
import { type FilledNoticeProps } from "./types";

export const NoticeFilled = ({
  content,
  details,
  action,
  expandable = true,
  expandedByDefault = false,
  type = NoticeType.BRAND,
  className,
  icon,
  justifyContent = false,
  dataTestId,
}: FilledNoticeProps) => {
  const { value: expanded, toggle } = useBoolean(expandedByDefault);

  const {
    iconComponent: IconComponent,
    iconColor,
    bgColor,
  } = filledTypeConfig[type];

  const handleToggle = () => {
    if (details && expandable) {
      toggle();
    }
  };

  const hasExpandableDetails = expandable && details;

  return (
    <div
      className="bg-background/primary rounded-companyBorderRadius"
      data-testid={dataTestId ? `${dataTestId}-notice` : "notice"}
    >
      <div
        className={cnTwMerge(
          "w-full p-3 rounded-companyBorderRadius",
          bgColor,
          className,
        )}
      >
        <div
          className={cnTwMerge("flex items-center justify-between", {
            "cursor-pointer": hasExpandableDetails,
          })}
          onClick={handleToggle}
        >
          <div className="flex gap-3 items-center justify-start flex-1 min-w-0">
            {!!icon && <div className={iconColor}>{icon}</div>}
            {!icon && !!IconComponent && (
              <IconComponent className={iconColor} />
            )}
            <span
              className={cnTwMerge(
                "w-full text-text/primary",
                expanded ? "body-base-semibold" : "body-base-medium",
                justifyContent ? "text-justify" : "text-left",
              )}
              data-testid={
                dataTestId ? `${dataTestId}-notice-content` : "notice-content"
              }
            >
              {content}
            </span>
          </div>
          {hasExpandableDetails || action ? (
            <div
              className="flex gap-3 items-center shrink-0"
              data-testid={
                dataTestId ? `${dataTestId}-notice-controls` : "notice-controls"
              }
            >
              {hasExpandableDetails ? (
                <ChevronIcon
                  className={cnTwMerge(
                    "defaultTransition text-text/secondary cursor-pointer",
                    {
                      "rotate-180 text-text/primary": expanded,
                    },
                  )}
                />
              ) : null}
              {action ? <div>{action}</div> : null}
            </div>
          ) : null}
        </div>
        {details && expanded ? <div className="pt-2">{details}</div> : null}
      </div>
    </div>
  );
};
